import React, { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import { ToastContainer } from 'react-toastify';
import DevOtpBanner from './components/dev/DevOtpBanner';
import 'react-toastify/dist/ReactToastify.css';
import { SnackbarProvider } from 'notistack';

// Context providers
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';

// Theme
import { createAppTheme } from './theme';
import { useAppTheme } from './context/ThemeContext';

// Layouts
import Layout from './components/layout/Layout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import RoleManagement from './pages/RoleManagement';
import PermissionCenter from './pages/PermissionCenter';
import TenantManagement from './pages/TenantManagement';
import OtpVerification from './pages/OtpVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Unauthorized from './pages/Unauthorized';
import Forbidden from './pages/Forbidden';
import HomeRedirect from './components/access/HomeRedirect';
import SimpleRolesList from './pages/Roles/SimpleRolesList';
// Import as needed when files are available
import CompanyManagement from './pages/CompanyManagement';
import SupplierManagement from './pages/SupplierManagement';
import SettingsManagement from './pages/SettingsManagement';
import SellerPlansManagement from './pages/SellerPlansManagement';
import AiReviewStatus from './pages/AiReviewStatus';
import UserbotCenter from './pages/UserbotCenter';
import TelegramChannelsManagement from './pages/TelegramChannelsManagement';
import AccountTypeManagement from './pages/AccountTypeManagement';
import CategoryManagement from './pages/CategoryManagement';
import SpecificationManagement from './pages/SpecificationManagement';
import ActivityAreaManagement from './pages/ActivityAreaManagement';
import RegularUserManagement from './pages/RegularUserManagement';
import StoreManagement from './pages/StoreManagement';
import StoreApplicationsManagement from './pages/StoreApplicationsManagement';
import ProductApprovalsManagement from './pages/ProductApprovalsManagement';
import ProductEditRequestsManagement from './pages/ProductEditRequestsManagement';
import StoreEditRequestsManagement from './pages/StoreEditRequestsManagement';
import OrderManagement from './pages/OrderManagement';
import DisputeManagement from './pages/DisputeManagement';
import DisputeCenter from './pages/DisputeCenter';
import ArbitrationPage from './pages/ArbitrationPage';
import ChatManagement from './pages/ChatManagement';
import ChatOversight from './pages/ChatOversight';
import FigaPointsSettings from './pages/FigaPointsSettings';
import AppUpdatesManagement from './pages/AppUpdatesManagement';
import AdminTicketDashboard from './pages/tickets/AdminTicketDashboard';
import AdminTicketList from './pages/tickets/AdminTicketList';
import AdminTicketDetail from './pages/tickets/AdminTicketDetail';
import CannedResponsesPage from './pages/tickets/CannedResponsesPage';
import TicketMacrosPage from './pages/tickets/TicketMacrosPage';
import AdvancedTicketMetricsPage from './pages/tickets/AdvancedTicketMetricsPage';
import AgentReportsPage from './pages/tickets/AgentReportsPage';
import NotificationsPage from './pages/NotificationsPage';
import AnnouncementManagement from './pages/AnnouncementManagement';
import AudienceMessaging from './pages/AudienceMessaging';
import PushSettingsPage from './pages/PushSettingsPage';
import BankAccountManagement from './pages/BankAccountManagement';
import WithdrawalManagement from './pages/WithdrawalManagement';
import UserWithdrawalManagement from './pages/UserWithdrawalManagement';
import FinanceDashboard from './pages/FinanceDashboard';
import FinanceTransactions from './pages/FinanceTransactions';
import FinanceStatements from './pages/FinanceStatements';
import FinanceReports from './pages/FinanceReports';
import FinanceAccounting from './pages/FinanceAccounting';
import FinanceInvoice from './pages/FinanceInvoice';

// Site Analytics pages
import SiteAnalyticsOverview from './pages/analytics/SiteAnalyticsOverview';
import SiteAnalyticsRealtime from './pages/analytics/SiteAnalyticsRealtime';
import SiteAnalyticsAcquisition from './pages/analytics/SiteAnalyticsAcquisition';
import SiteAnalyticsAudience from './pages/analytics/SiteAnalyticsAudience';
import SiteAnalyticsPlatforms from './pages/analytics/SiteAnalyticsPlatforms';
import SiteAnalyticsBehavior from './pages/analytics/SiteAnalyticsBehavior';
import SiteAnalyticsFunnels from './pages/analytics/SiteAnalyticsFunnels';
import SiteAnalyticsRetention from './pages/analytics/SiteAnalyticsRetention';
import SiteAnalyticsPerformance from './pages/analytics/SiteAnalyticsPerformance';
import SiteAnalyticsMarketplace from './pages/analytics/SiteAnalyticsMarketplace';
import SiteAnalyticsSearch from './pages/analytics/SiteAnalyticsSearch';
import SiteAnalyticsEngagement from './pages/analytics/SiteAnalyticsEngagement';
import SiteAnalyticsCampaigns from './pages/analytics/SiteAnalyticsCampaigns';
import KycList from './pages/KycList';
import KycDetail from './pages/KycDetail';
import BuyerRequestsManagement from './pages/BuyerRequestsManagement';
import LotteryManagement from './pages/LotteryManagement';
import RaffleManagement from './pages/RaffleManagement';
import SnappPayCenter from './pages/SnappPayCenter';
import DigipayCenter from './pages/DigipayCenter';
import ProductManagement from './pages/ProductManagement';
import PaymentLogsPage from './pages/PaymentLogsPage';

// Banner Pages
import BannerSlotManagement from './pages/BannerSlotManagement';
import BannerPendingApprovals from './pages/BannerPendingApprovals';
import AdminBannerManagement from './pages/AdminBannerManagement';
import SitePopupManagement from './pages/SitePopupManagement';
import BlogAdManagement from './pages/BlogAdManagement';
import FeatureFlagManagement from './pages/FeatureFlagManagement';
import StorePolicyManagement from './pages/StorePolicyManagement';
import ReviewReportsManagement from './pages/ReviewReportsManagement';
import MaintenanceWindowManagement from './pages/MaintenanceWindowManagement';

// New Admin Pages
import WalletAdminPage from './pages/WalletAdminPage';
import SellerBalanceSettingsPage from './pages/SellerBalanceSettingsPage';
import BackgroundJobsPage from './pages/BackgroundJobsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import MonitoringCenter from './pages/MonitoringCenter';
import ReviewsManagementPage from './pages/ReviewsManagementPage';
import StoryManagement from './pages/StoryManagement';
import UserActivityPage from './pages/UserActivityPage';
import SellerVerificationPage from './pages/SellerVerificationPage';

// Inspection Pages
import InspectorManagement from './pages/InspectorManagement';

// Work Assignment & Shift Management
import AdminWorkProfilesPage from './pages/AdminWorkProfiles';
import HolidayManagementPage from './pages/HolidayManagement';
import MyTasksPage from './pages/MyTasks';

import WholesaleManagement from './pages/WholesaleManagement';

// Article/Blog Pages
import ArticleManagement from './pages/ArticleManagement';
import ArticleEditRequests from './pages/ArticleEditRequests';
import ArticleCommentModeration from './pages/ArticleCommentModeration';
import ArticleCategoryManagement from './pages/ArticleCategoryManagement';
import ArticleTagManagement from './pages/ArticleTagManagement';
import ArticleSeriesManagement from './pages/ArticleSeriesManagement';
import ArticleReportManagement from './pages/ArticleReportManagement';

// Components
import ProtectedRoute from './components/common/ProtectedRoute';
// MENU_PERMISSION_MAP + MenuUpdater removed in Session 3 cutover. Static menu
// config now lives in `constants/adminMenus.ts` and `<ProtectedRoute>` derives
// permissions from there via `findLeafByPath`.
import AuthChecker from './components/common/AuthChecker';
import NavigationListener from './components/common/NavigationListener';
import ErrorHandlerSetup from './components/common/ErrorHandler';

// RTL setup for Material-UI with right-to-left support
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

function App() {
  const { theme: themeMode } = useAppTheme();
  const muiTheme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <AuthProvider>
          <ChatProvider>
          <NotificationProvider>
            <NavigationListener />
            <ErrorHandlerSetup />
            <AuthChecker />
            <ToastContainer position="bottom-left" rtl={true} />
            {process.env.NODE_ENV !== 'production' && <DevOtpBanner />}
            <SnackbarProvider>

              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/verify-otp" element={<OtpVerification />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* /unauthorized و /403 داخل Layout قرار دارند (پایین‌تر) تا کاربر
                    سایدبار را ببیند و بتواند به بخشی که دسترسی دارد برود؛ قبلاً
                    بیرون از Layout بودند و صفحه به بن‌بست می‌رسید. */}

                {/* /simple-roles قبلاً عمداً بدون حفاظت بود و لیست نقش‌ها را به هر
                    بازدیدکننده‌ای نشان می‌داد. حالا داخل Layout و پشت همان
                    پرمیشنی است که صفحهٔ اصلی نقش‌ها دارد. */}

                {/* Protected routes */}
                <Route element={<Layout />}>
                  {/* Dashboard - no specific permissions required */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />

                  {/* My assigned work — self-scoped, open to every admin */}
                  <Route path="/my-tasks" element={
                    <ProtectedRoute>
                      <MyTasksPage />
                    </ProtectedRoute>
                  } />

                  {/* User Management */}
                  <Route path="/users" element={
                    <ProtectedRoute>
                      <UserManagement />
                    </ProtectedRoute>
                  } />

                  {/* Regular User Management */}
                  <Route path="/regular-users" element={
                    <ProtectedRoute>
                      <RegularUserManagement />
                    </ProtectedRoute>
                  } />

                  {/* Role Management */}
                  <Route path="/roles" element={
                    <ProtectedRoute>
                      <RoleManagement />
                    </ProtectedRoute>
                  } />

                  {/* Permission Center — Grants + Catalog (Phase 4) */}
                  <Route path="/permission-center" element={
                    <ProtectedRoute requiredPermissions={['admin_grant_list', 'admin_grant_read']}>
                      <PermissionCenter />
                    </ProtectedRoute>
                  } />

                  {/* Tenant Management */}
                  <Route path="/tenants" element={
                    <ProtectedRoute>
                      <TenantManagement />
                    </ProtectedRoute>
                  } />



                  {/* Company Management - Uncomment when component is available */}
                  <Route path="/companies" element={
                    <ProtectedRoute>
                      <CompanyManagement />
                    </ProtectedRoute>
                  } />

                  {/* Suppliers & purchase invoices (gate derived from adminMenus '/suppliers') */}
                  <Route path="/suppliers" element={
                    <ProtectedRoute>
                      <SupplierManagement />
                    </ProtectedRoute>
                  } />

                  {/* Settings Management - Uncomment when component is available */}
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <SettingsManagement />
                    </ProtectedRoute>
                  } />

                  {/* Seller subscription plans (کمیسیون پلنی) */}
                  <Route path="/seller-plans" element={
                    <ProtectedRoute>
                      <SellerPlansManagement />
                    </ProtectedRoute>
                  } />

                  {/* دستیار گروه‌ها (حساب‌های شخصی تلگرام + AI) */}
                  <Route path="/userbot" element={
                    <ProtectedRoute>
                      <UserbotCenter />
                    </ProtectedRoute>
                  } />

                  {/* کانال‌ها و گروه‌های تلگرامِ متصل به فروشگاه‌ها */}
                  <Route path="/telegram-channels" element={
                    <ProtectedRoute>
                      <TelegramChannelsManagement />
                    </ProtectedRoute>
                  } />

                  {/* AI Review Status */}
                  <Route path="/ai-review" element={
                    <ProtectedRoute>
                      <AiReviewStatus />
                    </ProtectedRoute>
                  } />

                  {/* App Updates Management */}
                  <Route path="/app-updates" element={
                    <ProtectedRoute>
                      <AppUpdatesManagement />
                    </ProtectedRoute>
                  } />

                  {/* Activity Area Management */}
                  <Route path="/activity-areas" element={
                    <ProtectedRoute>
                      <ActivityAreaManagement />
                    </ProtectedRoute>
                  } />

                  {/* Account Type Management */}
                  <Route path="/account-types" element={
                    <ProtectedRoute>
                      <AccountTypeManagement />
                    </ProtectedRoute>
                  } />

                  {/* Category Management (mega-menu navigation tree) */}
                  <Route path="/categories" element={
                    <ProtectedRoute>
                      <CategoryManagement />
                    </ProtectedRoute>
                  } />

                  {/* Specifications — single page parametrized by `:type` route param
                      renders the CRUD UI for any of the ~50 spec collections
                      (cpu-brand, ram-type, display-panel, ...). */}
                  <Route path="/specifications" element={<Navigate to="/specifications/product-brand" replace />} />
                  <Route path="/specifications/:type" element={
                    <ProtectedRoute>
                      <SpecificationManagement />
                    </ProtectedRoute>
                  } />

                  {/* Store Management */}
                  <Route path="/stores" element={
                    <ProtectedRoute>
                      <StoreManagement />
                    </ProtectedRoute>
                  } />

                  {/* Store Applications Management */}
                  <Route path="/store-applications" element={
                    <ProtectedRoute>
                      <StoreApplicationsManagement />
                    </ProtectedRoute>
                  } />

                  {/* Product Approvals Management */}
                  <Route path="/product-approvals" element={
                    <ProtectedRoute>
                      <ProductApprovalsManagement />
                    </ProtectedRoute>
                  } />

                  {/* Product Edit Requests Management */}
                  <Route path="/product-edit-requests" element={
                    <ProtectedRoute>
                      <ProductEditRequestsManagement />
                    </ProtectedRoute>
                  } />

                  {/* Store Edit Requests Management */}
                  <Route path="/store-edit-requests" element={
                    <ProtectedRoute>
                      <StoreEditRequestsManagement />
                    </ProtectedRoute>
                  } />

                  {/* Order Management */}
                  <Route path="/orders" element={
                    <ProtectedRoute>
                      <OrderManagement />
                    </ProtectedRoute>
                  } />

                  {/* Unified Dispute Center */}
                  <Route path="/dispute-center" element={
                    <ProtectedRoute requiredPermissions={['admin_disputes_pending', 'admin_disputes_stats']}>
                      <DisputeCenter />
                    </ProtectedRoute>
                  } />

                  {/* Arbitration Panel */}
                  <Route path="/arbitration" element={
                    <ProtectedRoute requiredPermissions={['admin_disputes_pending', 'admin_disputes_stats']}>
                      <ArbitrationPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/arbitration/:chatId" element={
                    <ProtectedRoute requiredPermissions={['admin_disputes_pending', 'admin_disputes_stats']}>
                      <ArbitrationPage />
                    </ProtectedRoute>
                  } />

                  {/* Dispute Management (Legacy) */}
                  <Route path="/disputes" element={
                    <ProtectedRoute>
                      <DisputeManagement />
                    </ProtectedRoute>
                  } />


                  {/* Buyer Requests Management (درخواست‌های خرید — مارکت‌پلیس معکوس) */}
                  <Route path="/buyer-requests" element={
                    <ProtectedRoute>
                      <BuyerRequestsManagement />
                    </ProtectedRoute>
                  } />

                  {/* Lottery Management (قرعه‌کشی‌های مایلستونی) */}
                  <Route path="/lottery" element={
                    <ProtectedRoute>
                      <LotteryManagement />
                    </ProtectedRoute>
                  } />

                  {/* Raffle (قرعه‌کشی وزنی کاربران) */}
                  <Route path="/raffle" element={
                    <ProtectedRoute>
                      <RaffleManagement />
                    </ProtectedRoute>
                  } />

                  {/* SnappPay Center (مرکز اسنپ‌پی — مدیریت درگاه خرید اقساطی) */}
                  <Route path="/snapppay" element={
                    <ProtectedRoute requiredPermissions="admin_snapppay_read">
                      <SnappPayCenter />
                    </ProtectedRoute>
                  } />

                  {/* DigiPay Center (مرکز دیجی‌پی — درگاه یکپارچه: بانکی / کیف پول / اقساطی) */}
                  <Route path="/digipay" element={
                    <ProtectedRoute requiredPermissions="admin_digipay_read">
                      <DigipayCenter />
                    </ProtectedRoute>
                  } />


                  {/* Product Management */}
                  <Route path="/products" element={
                    <ProtectedRoute>
                      <ProductManagement />
                    </ProtectedRoute>
                  } />

                  {/* KYC Management */}
                  <Route path="/kyc" element={
                    <ProtectedRoute>
                      <KycList />
                    </ProtectedRoute>
                  } />
                  <Route path="/kyc/:id" element={
                    <ProtectedRoute>
                      <KycDetail />
                    </ProtectedRoute>
                  } />

                  {/* Chat Management */}
                  <Route path="/chats" element={
                    <ProtectedRoute>
                      <ChatManagement />
                    </ProtectedRoute>
                  } />

                  {/* Chat Oversight — نظارت زنده بر همه چت‌ها */}
                  <Route path="/chat-oversight" element={
                    <ProtectedRoute>
                      <ChatOversight />
                    </ProtectedRoute>
                  } />

                  {/* GelaPoints Settings */}
                  <Route path="/figapoints-settings" element={
                    <ProtectedRoute>
                      <FigaPointsSettings />
                    </ProtectedRoute>
                  } />

                  {/* Bank Account Management */}
                  <Route path="/bank-accounts" element={
                    <ProtectedRoute>
                      <BankAccountManagement />
                    </ProtectedRoute>
                  } />

                  {/* Withdrawal Management */}
                  <Route path="/withdrawals" element={
                    <ProtectedRoute>
                      <WithdrawalManagement />
                    </ProtectedRoute>
                  } />

                  {/* Finance Dashboard */}
                  <Route path="/finance" element={
                    <ProtectedRoute requiredPermissions={[ 'admin_finance_overview' ]}>
                      <FinanceDashboard />
                    </ProtectedRoute>
                  } />

                  {/* Finance — unified ledger / statements / periodic reports */}
                  <Route path="/finance/transactions" element={
                    <ProtectedRoute requiredPermissions={[ 'admin_finance_ledger' ]}>
                      <FinanceTransactions />
                    </ProtectedRoute>
                  } />
                  <Route path="/finance/statements" element={
                    <ProtectedRoute requiredPermissions={[ 'admin_finance_statement' ]}>
                      <FinanceStatements />
                    </ProtectedRoute>
                  } />
                  <Route path="/finance/reports" element={
                    <ProtectedRoute requiredPermissions={[ 'admin_finance_reports' ]}>
                      <FinanceReports />
                    </ProtectedRoute>
                  } />
                  {/* 🧾 حسابداری: سود و زیان / ترازنامه / مالیات / دفتر فروش / بدهی فروشندگان / مغایرت‌گیری / بستن دوره + صورتحساب چاپی */}
                  <Route path="/finance/accounting" element={
                    <ProtectedRoute requiredPermissions={[ 'admin_finance_accounting' ]}>
                      <FinanceAccounting />
                    </ProtectedRoute>
                  } />
                  <Route path="/finance/invoice/:orderId" element={
                    <ProtectedRoute requiredPermissions={[ 'admin_finance_accounting' ]}>
                      <FinanceInvoice />
                    </ProtectedRoute>
                  } />

                  {/* ===== Site Analytics ===== */}
                  <Route path="/analytics" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_overview']}>
                      <SiteAnalyticsOverview />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/realtime" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_realtime']}>
                      <SiteAnalyticsRealtime />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/acquisition" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_acquisition']}>
                      <SiteAnalyticsAcquisition />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/audience" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_audience']}>
                      <SiteAnalyticsAudience />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/platforms" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_platforms']}>
                      <SiteAnalyticsPlatforms />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/behavior" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_behavior']}>
                      <SiteAnalyticsBehavior />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/funnels" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_funnels']}>
                      <SiteAnalyticsFunnels />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/retention" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_retention']}>
                      <SiteAnalyticsRetention />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/performance" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_performance']}>
                      <SiteAnalyticsPerformance />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/marketplace" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_marketplace']}>
                      <SiteAnalyticsMarketplace />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/engagement" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_engagement']}>
                      <SiteAnalyticsEngagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/search" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_search']}>
                      <SiteAnalyticsSearch />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/campaigns" element={
                    <ProtectedRoute requiredPermissions={['admin_analytics_campaigns']}>
                      <SiteAnalyticsCampaigns />
                    </ProtectedRoute>
                  } />

                  {/* Payment Logs */}
                  <Route path="/payment-logs" element={
                    <ProtectedRoute>
                      <PaymentLogsPage />
                    </ProtectedRoute>
                  } />

                  {/* User Withdrawal Management */}
                  <Route path="/user-withdrawal-requests" element={
                    <ProtectedRoute>
                      <UserWithdrawalManagement />
                    </ProtectedRoute>
                  } />

                  {/* Ticket Management */}
                  <Route path="/tickets" element={
                    <ProtectedRoute>
                      <AdminTicketDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/tickets/list" element={
                    <ProtectedRoute>
                      <AdminTicketList />
                    </ProtectedRoute>
                  } />
                  {/* Static ticket sub-pages to prevent catching by /tickets/:id */}
                  <Route path="/tickets/canned-responses" element={
                    <ProtectedRoute>
                      <CannedResponsesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/tickets/macros" element={
                    <ProtectedRoute>
                      <TicketMacrosPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/tickets/metrics/advanced" element={
                    <ProtectedRoute>
                      <AdvancedTicketMetricsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/tickets/reports/agents" element={
                    <ProtectedRoute>
                      <AgentReportsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/tickets/:id" element={
                    <ProtectedRoute>
                      <AdminTicketDetail />
                    </ProtectedRoute>
                  } />

                  {/* Notifications Page */}
                  <Route path="/notifications" element={
                    <ProtectedRoute>
                      <NotificationsPage />
                    </ProtectedRoute>
                  } />

                  {/* Broadcast announcements (push + in-app) */}
                  <Route path="/admin/announcements" element={
                    <ProtectedRoute requiredPermissions="admin_announcement_read">
                      <AnnouncementManagement />
                    </ProtectedRoute>
                  } />

                  {/* پیام به دستهٔ کاربران — سازندهٔ مخاطب قاعده‌محور + ارسال چندکاناله */}
                  <Route path="/admin/audience-messaging" element={
                    <ProtectedRoute requiredPermissions="admin_announcement_read">
                      <AudienceMessaging />
                    </ProtectedRoute>
                  } />

                  {/* Push notification status + per-admin preferences.
                      No permission gate: these are the signed-in admin's own
                      delivery settings, not someone else's data. */}
                  <Route path="/admin/push-settings" element={
                    <ProtectedRoute>
                      <PushSettingsPage />
                    </ProtectedRoute>
                  } />

                  {/* Banner Management */}
                  <Route path="/admin/banners/slots" element={
                    <ProtectedRoute>
                      <BannerSlotManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/banners/pending-approvals" element={
                    <ProtectedRoute>
                      <BannerPendingApprovals />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/banners/my-banners" element={
                    <ProtectedRoute>
                      <AdminBannerManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/popups" element={
                    <ProtectedRoute>
                      <SitePopupManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/blog-ads" element={
                    <ProtectedRoute>
                      <BlogAdManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/feature-flags" element={
                    <ProtectedRoute>
                      <FeatureFlagManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/store-policies" element={
                    <ProtectedRoute>
                      <StorePolicyManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/review-reports" element={
                    <ProtectedRoute>
                      <ReviewReportsManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/maintenance-windows" element={
                    <ProtectedRoute>
                      <MaintenanceWindowManagement />
                    </ProtectedRoute>
                  } />

                  {/* Wallet Admin */}
                  <Route path="/wallet-admin" element={
                    <ProtectedRoute>
                      <WalletAdminPage />
                    </ProtectedRoute>
                  } />

                  {/* Seller Balance Settings */}
                  <Route path="/seller-balance-settings" element={
                    <ProtectedRoute>
                      <SellerBalanceSettingsPage />
                    </ProtectedRoute>
                  } />

                  {/* Background Jobs */}
                  <Route path="/background-jobs" element={
                    <ProtectedRoute>
                      <BackgroundJobsPage />
                    </ProtectedRoute>
                  } />

                  {/* Audit Logs */}
                  <Route path="/audit-logs" element={
                    <ProtectedRoute>
                      <AuditLogsPage />
                    </ProtectedRoute>
                  } />

                  {/* مرکز پایش — سلامت زنده، خطاها، حادثه‌ها و لاگ خام.
                      ⚠️ این نمای «عمیق» است و فقط وقتی کار می‌کند که بک‌اند بالا باشد.
                      برای دیدن وضعیت وقتی همه‌چیز خوابیده، داشبورد نگهبان مستقل
                      (پوشهٔ monitoring/) را باز کنید — آن یکی به هیچ‌چیزِ اینجا وابسته نیست. */}
                  <Route path="/monitoring" element={
                    <ProtectedRoute>
                      <MonitoringCenter />
                    </ProtectedRoute>
                  } />

                  {/* Reviews Management */}
                  <Route path="/reviews" element={
                    <ProtectedRoute>
                      <ReviewsManagementPage />
                    </ProtectedRoute>
                  } />

                  {/* Story Management */}
                  <Route path="/stories" element={
                    <ProtectedRoute>
                      <StoryManagement />
                    </ProtectedRoute>
                  } />

                  {/* User Activity */}
                  <Route path="/user-activity" element={
                    <ProtectedRoute>
                      <UserActivityPage />
                    </ProtectedRoute>
                  } />

                  {/* Seller Verification */}
                  <Route path="/seller-verifications" element={
                    <ProtectedRoute>
                      <SellerVerificationPage />
                    </ProtectedRoute>
                  } />

                  {/* Inspection Management */}
                  <Route path="/inspections" element={
                    <ProtectedRoute>
                      <InspectorManagement />
                    </ProtectedRoute>
                  } />

                  {/* Work Assignment & Shifts */}
                  <Route path="/admin-work-profiles" element={
                    <ProtectedRoute>
                      <AdminWorkProfilesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/holidays" element={
                    <ProtectedRoute>
                      <HolidayManagementPage />
                    </ProtectedRoute>
                  } />

                  {/* Article/Blog Management */}
                  <Route path="/articles" element={
                    <ProtectedRoute>
                      <ArticleManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/article-edit-requests" element={
                    <ProtectedRoute>
                      <ArticleEditRequests />
                    </ProtectedRoute>
                  } />
                  <Route path="/article-comments" element={
                    <ProtectedRoute>
                      <ArticleCommentModeration />
                    </ProtectedRoute>
                  } />
                  <Route path="/article-categories" element={
                    <ProtectedRoute>
                      <ArticleCategoryManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/article-tags" element={
                    <ProtectedRoute>
                      <ArticleTagManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/article-series" element={
                    <ProtectedRoute>
                      <ArticleSeriesManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/article-reports" element={
                    <ProtectedRoute>
                      <ArticleReportManagement />
                    </ProtectedRoute>
                  } />


                  {/* Wholesale Management — Phase 7 */}
                  <Route path="/wholesale" element={
                    <ProtectedRoute>
                      <WholesaleManagement />
                    </ProtectedRoute>
                  } />

                  <Route path="/simple-roles" element={
                    <ProtectedRoute requiredPermissions={['admin_role_read']}>
                      <SimpleRolesList />
                    </ProtectedRoute>
                  } />

                  {/* صفحات خطای دسترسی — داخل Layout تا سایدبار در دسترس بماند */}
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="/403" element={<Forbidden />} />

                  {/* مقصد ورود: اولین منویی که کاربر واقعاً به آن دسترسی دارد */}
                  <Route path="/" element={<HomeRedirect />} />

                  {/* Catch-all route to redirect to unauthorized if the route doesn't exist */}
                  <Route path="*" element={<Navigate to="/unauthorized" />} />
                </Route>
              </Routes>
            </SnackbarProvider>
          </NotificationProvider>
        </ChatProvider>
        </AuthProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App; 